import { AfterViewInit, Component, OnInit, Output, ViewChild, EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { skip, takeWhile } from 'rxjs/operators';
import { IMPORT_TXN } from 'app/constants';
import { forkJoin } from 'rxjs';
import { reduceFileNameLength, validateFor, validateIfArray } from 'app/common-functions';
import { BusinessService, HttpService, NotificationService, TreeService } from 'app/shared/services';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { API_END_POINT, BE_KEY } from '../../../../../constants/index';
import {
	FIELD_IS_REQUIRED_ERR_MSG,
	FILE_NOT_SUPPORTED_MSG,
	PAYLOAD_KEYS,
	REQUEST_SUBMITTED_SUCCESFULLY,
	SELECT_REQUIRED_FIELDS,
	SOMETHING_WENT_WRONG_ERR_MSG,
} from './constants';
import { createFormData } from './helper-functions';

const ERROR: string = 'Error';
const SUCCESS: string = 'Success';
const STORE_ID_ERR: string = 'Could not set storeID. Please try again.';
const NO_TERMINAL_ERR_MSG: string = 'There are no terminals under this Profit Center. Please choose different Profit Center.';
export const ALLOWED_FILE_FORMATS = ['.txt', '.csv', '.eft', '.EFT'];
@Component({
	selector: 'app-import-transactions-modal',
	templateUrl: './import-transactions-modal.component.html',
	styleUrls: ['./import-transactions-modal.component.scss'],
})
export class ImportTransactionsModalComponent implements OnInit, OnChanges, AfterViewInit {
	@ViewChild('importTxnModal')
	public importTxnModal: ModalDirective; //modal
	@Output()
	public closeModalEvent = new EventEmitter<boolean>(); // event emitted when modal is closed
	@Output()
	public refreshGridEvent = new EventEmitter<boolean>();
	@Input() showImportTxnModal: boolean = false;
	@Input() showInfo: boolean = false; //Show info related to import txn action when opened from outstanding txn
	pctrName: string = ''; //profit center name
	GMPW: string = ''; //GMPW of selected profit center
	GMID: string = ''; //GMIE of selected profit center
	terminalDropDownArr: any[] = []; //Array containing terminal data
	defDropDownArray: any[] = []; //Array containig definition files data
	selectedTermID: string = ''; //TerminalID after selecting in drop down
	selectedGTID: string = ''; //GTID of selected terminal
	pCtrID: string = ''; //MongoID of profit center
	uploadedFile: any; //File selected for processing
	selectedDefFileID: string = ''; //Selected definition file id
	modalLoader: boolean = false; //Boolean to check if data is loaded
	form: FormGroup; //form
	reduceFileNameLength = reduceFileNameLength; //function which reduces files having large name
	errors = {
		importFile: '',
	}; //Object for handling form errors
	parameters: any = {};
	alive: boolean = true;
	submitting: boolean = false;

	showPreview: boolean = false;
	previewData: any = null;
	fileReader = new FileReader();

	constructor(
		private treeService: TreeService,
		private httpService: HttpService,
		private notificationService: NotificationService,
		private businessService: BusinessService,
		private fb: FormBuilder
	) {}

	/**
	 * On click of import button in grid, check for temrinal and open modal
	 * @param {SimpleChanges} changes
	 * @memberof ImportTransactionsModalComponent
	 */
	async ngOnChanges(changes: SimpleChanges) {
		if (changes.hasOwnProperty('showImportTxnModal') && !changes.showImportTxnModal.firstChange && changes.showImportTxnModal.currentValue) {
			try {
				this.setPCtrData();
				let hasTerminal = await this.fetchTerminalAndDefFiles(this.pCtrID);
				if (hasTerminal && this.showImportTxnModal) {
					this.importTxnModal.show();
				}
			} catch (err) {
				this.notificationService.error(err, ERROR);
				this.closeModalEvent.emit(true);
			}
		}
	}

	ngOnInit() {
		this.toFormGroup();
		this.businessService.watchBusinessID$
			.pipe(
				takeWhile(() => this.alive),
				skip(1)
			)
			.subscribe((bizID) => {
				if (bizID) {
					this.form.patchValue({ xBusinessID: bizID });
				}
			});
		this.treeService.profitCenterID.subscribe(async (pctrID) => {
			if (!pctrID) {
				// console.log('no pctrID: returning from import txn modal component');
				return;
			}
			this.pCtrID = pctrID;
			this.form.patchValue({ xPCtrID: this.pCtrID });
		});
	}

	/**
	 * Init form group
	 * @memberof ImportTransactionsModalComponent
	 */
	toFormGroup() {
		this.form = this.fb.group({
			xTerminalID: ['', Validators.required],
			xPCtrID: [this.pCtrID],
			xBusinessID: [this.businessService.businessID.getValue()],
			xStoreID: [''],
			importFile: ['', Validators.required],
			definitionFileID: ['', Validators.required],
		});
	}

	/**
	 * Extract terminal data from api response and set to terminal drop down
	 * @param {any} terminalsResponse
	 * @memberof ImportTransactionsModalComponent
	 */
	setTerminalDropDown(terminalsResponse: any) {
		this.terminalDropDownArr = validateFor(BE_KEY.data, terminalsResponse) ? terminalsResponse[BE_KEY.data] : [];
		this.selectedTermID = this.terminalDropDownArr.length ? this.terminalDropDownArr[0]._id : '';
	}

	/**
	 * Create query and fetch terminal and definition files data
	 * @param {string} pctrID
	 * @memberof ImportTransactionsModalComponent
	 */
	fetchTerminalAndDefFiles(pctrID: string): Promise<any> {
		return new Promise((resolve, reject) => {
			let terminalQuery = {
				xMainPCtrID: pctrID,
				noGrid: true,
				selectKey: `${PAYLOAD_KEYS.xName},${PAYLOAD_KEYS.xGTID},${PAYLOAD_KEYS.xStoreID}`,
			};
			this.modalLoader = true;
			let observable = [
				this.httpService.getAll(API_END_POINT.xpcTerminals, terminalQuery),
				this.httpService.getAll(API_END_POINT.importTxnDefinition, { noGrid: true }),
			];
			forkJoin(observable).subscribe(
				(response: any) => {
					// console.log('fetchTerminalAndDefFiles: response', response);
					this.modalLoader = false;
					if (!validateIfArray(response)) {
						return reject(SOMETHING_WENT_WRONG_ERR_MSG);
					}
					let termResp = response[0];
					// console.log('termResp: fetchTerminalAndDefFiles', termResp);
					if (!termResp || !termResp.data || !termResp.data.length) {
						return reject(NO_TERMINAL_ERR_MSG);
					}
					let defFielsResp = response.length > 1 ? response[1] : {};
					this.setTerminalDropDown(termResp);
					this.setDefFilesDropDown(defFielsResp);
					this.setStoreID(termResp);
					return resolve(true);
				},
				(error) => {
					console.log('error: fetchTerminalAndDefFiles', error);
					this.modalLoader = false;
					let msg = validateFor(BE_KEY.message, error) ? error[BE_KEY.message] : SOMETHING_WENT_WRONG_ERR_MSG;
					// this.notificationService.error(msg, ERROR);
					return reject(msg);
				}
			);
		});
	}

	/**
	 * Set GMPW and pctr names from pctr data
	 *
	 * @memberof ImportTransactionsModalComponent
	 */
	setPCtrData() {
		this.pctrName = validateFor(PAYLOAD_KEYS.xName, this.treeService.currentClickedNodeData)
			? this.treeService.currentClickedNodeData[PAYLOAD_KEYS.xName]
			: '';
		this.GMPW = validateFor(PAYLOAD_KEYS.xGMPW, this.treeService.currentClickedNodeData) ? this.treeService.currentClickedNodeData[PAYLOAD_KEYS.xGMPW] : '';
		this.GMID = validateFor(PAYLOAD_KEYS.xGMID, this.treeService.currentClickedNodeData) ? this.treeService.currentClickedNodeData[PAYLOAD_KEYS.xGMID] : '';
	}

	/**
	 * Set definition files drop down array from api response
	 * @param {object} defFilesResp api response
	 * @memberof ImportTransactionsModalComponent
	 */
	setDefFilesDropDown(defFilesResp: any) {
		this.defDropDownArray = validateFor(BE_KEY.data, defFilesResp) ? defFilesResp[BE_KEY.data] : [];
		this.selectedDefFileID = this.defDropDownArray.length ? this.defDropDownArray[0].id : '';
		this.onSelectTerminal();
	}

	/**
	 * On selecting definition file, clear form error
	 * @memberof ImportTransactionsModalComponent
	 */
	onSelectDefFile() {
		console.log('def file selected');
	}

	/**
	 * On selecting terminal, set GTID and clear form error
	 * @memberof ImportTransactionsModalComponent
	 */
	onSelectTerminal() {
		let terminal = this.terminalDropDownArr.find((terminal) => terminal._id === this.selectedTermID);
		this.selectedGTID = terminal[PAYLOAD_KEYS.xGTID];
	}

	setStoreID(termResp: any) {
		let terminalData = validateFor(BE_KEY.data, termResp) ? termResp[BE_KEY.data] : [];
		let storeID = terminalData.length ? terminalData[0][PAYLOAD_KEYS.xStoreID] : '';
		if (!storeID) {
			this.notificationService.error(STORE_ID_ERR, ERROR);
			return;
		}
		this.form.patchValue({
			xStoreID: storeID,
		});
	}

	/**
	 * Validate form data before submission and set form error
	 *
	 * @returns
	 * @memberof ImportTransactionsModalComponent
	 */
	validateFormData() {
		let hasError = false;
		if (!this.form.value.importFile) {
			hasError = true;
			this.errors.importFile = FIELD_IS_REQUIRED_ERR_MSG;
		}
		if (this.form.value.importFile && !ALLOWED_FILE_FORMATS.includes('.' + this.form.value.importFile.name.split('.').pop())) {
			hasError = true;
			this.errors.importFile = FILE_NOT_SUPPORTED_MSG + ALLOWED_FILE_FORMATS.toString();
		}
		if (hasError) {
			this.notificationService.error(SELECT_REQUIRED_FIELDS, ERROR);
		}
		return hasError;
	}

	async onPreview() {
		if (this.validateFormData()) return;
		try {
			this.submitting = true;
			const preview = await this.readFileContent(this.uploadedFile);
			this.previewData = {
				...preview,
				fileName: this.uploadedFile.name,
				fileSize: this.uploadedFile.size,
				uploadTime: new Date()
			};
			this.showPreview = true;
		} catch (error) {
			this.notificationService.error('read file failed: ' + error.message, "Error");
		} finally {
			this.submitting = false;
		}
	}

	private getFileDefination() {
		let fileDefine = this.form.value[IMPORT_TXN.definitionFileID];
		let SkipRows = 0;
		let AmountColmName = '';
		if (fileDefine == 'pgccfg_svctxn_txnimp_c9pg_token') {
			SkipRows = 2;
			AmountColmName = 'Total';
		}
		else if (fileDefine == 'pgccfg_svctxn_txnimp_nacha_ach') {
			SkipRows = 0;
			AmountColmName = 'Total Amount';
		}
		else if (fileDefine == 'pgccfg_svctxn_txnimp_c9pg_ach'
			|| fileDefine == 'pgccfg_svctxn_txnimp_paymuse_ach') {
			SkipRows = 0;
			AmountColmName = 'Charge';
		}
		else if (fileDefine == 'pgccfg_svctxn_txnimp_c9pg_cc'
			|| fileDefine == 'pgccfg_svctxn_txnimp_crystalfalls_cc') {
			SkipRows = 2;
			AmountColmName = 'Total';
		}
		return {
			SkipRows,
			AmountColmName,
		}
	}

	private readFileContent(file: File): Promise<any> {
		return new Promise((resolve, reject) => {
			if (file.size > 2 * 1024 * 1024) {
				throw new Error('File exceeds 2MB limit.');
			}
			const extension = file.name.split('.').pop().toLowerCase();
			this.fileReader.onload = (e: any) => {
				try {
					let content = e.target.result;
					content = content.replace(/"((?:\\"|.)*?)"/g, (match, str) => {
						return `"${str.replace(/,/g, '')}"`; // remove all comma
					});

					if (extension === 'csv') {
						let fileDefination = this.getFileDefination();

						const allLines = content.split('\n').filter(line => line.trim() !== '');
						if (allLines.length < fileDefination.SkipRows + 1) throw new Error('Invalid file format, Incorrect number of header rows');

						const headers = allLines[fileDefination.SkipRows].split(',').map(h => h.trim());
						const totalIndex = headers.findIndex(h => h === fileDefination.AmountColmName);
						if (totalIndex === -1) throw new Error(`Invalid file format,column name ${fileDefination.AmountColmName} not found`);

						let totalRows = 0;
						let totalAmount = 0;
						const rows = [];

						for (let i = fileDefination.SkipRows + 1; i < allLines.length; i++) {
							let line = allLines[i];
							// line = line.replace(/"((?:\\"|.)*?)"/g, (match, str) => {
							// 	console.log("xxxx-->", str);
							// 	return `"${str.replace(/,/g, '')}"`; // remove all comma
							// 	// return `"${str.replace(/(\d),(?=\d{3}\b)/g, '$1')}"`;
							//   });
							const cells = line.split(',').map(c => c.trim().replace(/"/g, ''));
							if (cells.length > totalIndex) {
								const amount = parseFloat(cells[totalIndex].replace(/[$,]/g, ''));
								if (!isNaN(amount)) {
									totalRows++;
									totalAmount += amount;
									rows.push(cells);
								}
							}
						}

						resolve({
							type: 'csv',
							headers,
							rows: rows.slice(0, 1000),
							stats: {
								totalRows,
								totalAmount: +totalAmount.toFixed(2) 
							}
						});
					}
					else if (['txt', 'eft'].includes(extension)) {
						resolve({ type: 'text', content: content.slice(0, 5000) }); // limit length
					}
					else {
						resolve({ type: 'binary' });
					}
				} catch (error) {
					reject(error);
				}
			};

			this.fileReader.onerror = (e) => {
				console.error('File Reader Error:', e.target?.error);
				reject(new Error('Please re-upload the modified file if the file has been modified.'));
			};

			if (['csv', 'txt', 'eft'].includes(extension)) {
				this.fileReader.readAsText(file);
			} else {
				this.fileReader.readAsArrayBuffer(file);
			}
		});
	}

	cancelPreview() {
		this.showPreview = false;
		this.previewData = null;
	}

	/**
	 * Handle form submit
	 * @returns
	 * @memberof ImportTransactionsModalComponent
	 */
	 confirmSubmission() {
		this.submitting = true;
		// if (this.validateFormData()) {
		// 	this.submitting = false;
		// 	return;
		// }
		let formData = createFormData(this.form);
		this.createTxns(formData);
	}

	/**
	 * Hit api for creating txns and show success/error results
	 * @param {object} payload payload for creating transactions
	 * @memberof ImportTransactionsModalComponent
	 */
	createTxns(payload: any) {
		this.httpService.store(API_END_POINT.importTransaction, payload).subscribe(
			(importTxnResp) => {
				// console.log('createTxns: importTxnsResp', importTxnResp);
				this.closeModal();
				this.refreshGridEvent.emit(true);
				this.notificationService.success(REQUEST_SUBMITTED_SUCCESFULLY, SUCCESS);
			},
			(importTxnsError) => {
				console.log('createTxns: importTxnsError', importTxnsError);
				this.closeModal();
				let errMsg = validateFor(BE_KEY.message, importTxnsError) ? importTxnsError[BE_KEY.message] : SOMETHING_WENT_WRONG_ERR_MSG;
				this.notificationService.error(errMsg, ERROR);
			}
		);
	}

	/**
	 * Handle the file uploaded by user and update the form data, clear error
	 * @param {FileList} file
	 * @memberof ImportTransactionsModalComponent
	 */
	fileUpload(file: FileList) {
		this.uploadedFile = file.item(0);
		this.form.patchValue({
			importFile: this.uploadedFile,
		});
		this.errors.importFile = '';
	}

	ngAfterViewInit() {
		if (this.showImportTxnModal) {
			this.importTxnModal.show();
		}
	}

	/**
	 * Close import transactions modal
	 * @memberof ImportTransactionsModalComponent
	 */
	closeModal() {
		this.importTxnModal.hide();
		this.form.patchValue({ importFile: '' });
		this.errors.importFile = '';
		this.uploadedFile = '';
		this.submitting = false;
		this.showPreview = false;
		this.closeModalEvent.emit(true);
	}


printPreview() {
	const printContent = this.generatePrintableContent();
	const printWindow = window.open('', '_blank');
	printWindow.document.write(`
	  <html>
		<head>
		  <title>Transaction Preview - Print</title>
		  <style>
			${this.getPrintStyles()}
		  </style>
		</head>
		<body onload="window.print();window.close()">
		  ${printContent}
		</body>
	  </html>
	`);
	printWindow.document.close();
  }
  
  private generatePrintableContent(): string {
	if (!this.previewData) return '';
  
	let content = `
	  <div class="print-header">
		<h2>Transaction Import Preview</h2>
		<div class="metadata">
		  <div>File: ${this.previewData.fileName}</div>
		  <div>Uploaded: ${this.previewData.uploadTime.toLocaleString()}</div>
		</div>
	  </div>
	`;
  
	if (this.previewData.type === 'csv') {
	  content += this.generateCSVPrintableContent();
	} else if (this.previewData.type === 'text') {
	  content += this.generateTextPrintableContent();
	} else {
	  content += '<div class="print-alert">Binary file content cannot be printed</div>';
	}
  
	return content;
  }
  
  private generateCSVPrintableContent(): string {
	return `
	  <div class="print-summary">
		<div>Total Records: ${this.previewData.stats.totalRows}</div>
		<div>Total Amount: $${this.previewData.stats.totalAmount.toFixed(2)}</div>
	  </div>  
	  <table class="print-table">
		<thead>
		  <tr>${this.previewData.headers.map(h => `<th>${h}</th>`).join('')}</tr>
		</thead>
		<tbody>
		  ${this.previewData.rows.map(row => `
			<tr>
			  ${row.map(cell => `<td>${cell}</td>`).join('')}
			</tr>
		  `).join('')}
		</tbody>
	  </table>
	`;
  }
  
  private generateTextPrintableContent(): string {
	return `
	  <pre class="print-text">${this.previewData.content}</pre>
	`;
  }
  
  private getPrintStyles(): string {
	return `
	  body { 
		font-family: Arial, sans-serif; 
		margin: 0.5cm 0.5cm 0.5cm;
		color: #000;
	  }
	  .print-header {
		margin: 0 0 10px 0; 
		padding: 0;         
	  }
	  .metadata div {
		margin: 5px 0;
		color: #666;
	  }
	  .print-table {
		width: 100%;
		border-collapse: collapse;
		margin-top: 15px;
	  }
	  .print-table th, .print-table td {
		border: 1px solid #ddd;
		padding: 8px;
		text-align: left;
	  }
	  .print-table th {
		background-color: #f5f5f5;
	  }
	  .print-text {
		white-space: pre-wrap;
		font-family: monospace;
		background-color: #f8f9fa;
		padding: 15px;
		border-radius: 4px;
	  }
	  .print-summary {
		margin: 15px 0;
		padding: 10px;
		background: #f8f9fa;
		border: 1px solid #eee;
	  }
	  .print-alert {
		color: #856404;
		background-color: #fff3cd;
		padding: 15px;
		border-radius: 4px;
	  }
	  @page {
		margin: 1cm;
	  }
	`;
  }

  getSampleContent(fileDefine:String){
	let sampleContent = '';
	if (fileDefine == 'pgccfg_svctxn_txnimp_c9pg_token') {
		sampleContent = `C9PG Transaction Import by Token (Member Number),,,,,,,,
,,,,,,,,
Mbr #,Total,Invoice #
2387,2342.66,807942
1311,1035.76,807907`;
	}
	else if (fileDefine == 'pgccfg_svctxn_txnimp_nacha_ach') {
		sampleContent = 'To be done';
	}
	else if (fileDefine == 'pgccfg_svctxn_txnimp_c9pg_ach'
		|| fileDefine == 'pgccfg_svctxn_txnimp_paymuse_ach') {
			sampleContent = `Contact,Routing,Account,Charge,Invoice #
tom@abc.com,222222255,987654321,2342.66,807942
tina@abc.com,222222255,98700001,104.6,807943`;
	}
	else if (fileDefine == 'pgccfg_svctxn_txnimp_c9pg_cc'
		|| fileDefine == 'pgccfg_svctxn_txnimp_crystalfalls_cc') {
			sampleContent = `Crystal Falls Credit Card Payments (Monthly),,,2/14/2023,,,,,,,,
,,,,,,,,,,,
Mbr #,Total,Credit Card #,Exp,CVV,Name,Invoice #
2387,2342.66,4000225720169908,7/26,951,Tom Smith,807942
1311,104.25,4005520000000129,7/26,951,Robert Thomas,807907`;
	}
	return	  sampleContent;

  }

downloadSample() {
	let fileDefine = this.form.value[IMPORT_TXN.definitionFileID];

	// sample file context CSV
	const sampleContent = this.getSampleContent(fileDefine);
  
	const blob = new Blob([sampleContent], { type: 'text/csv' });
  
	// create download link
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;	
	
	let sampleType = this.defDropDownArray.filter(index => index.id == fileDefine);
	a.download = sampleType.length > 0 ? `${sampleType[0].name}_transaction_sample.csv` : 'transaction_sample.csv';

	// trigg download
	a.click();
	
	// clear resource
	window.URL.revokeObjectURL(url);
	a.remove();
  }  

}
